import React, { useState, useEffect, useCallback, useContext } from "react";
import ReactDOM from "react-dom";
import { Loading, IconButton } from "@alm/flow-ds/components";
import FocusLock, { AutoFocusInside } from "react-focus-lock";
import { useTranslation } from "react-i18next";
import { WorkbenchContext } from "../../contexts/WorkbenchContext";

interface Props {
	close: () => void;
	children: React.ReactNode;
	loadingContent: boolean;
	title: string;
}

function EditForm({ children, close, loadingContent, title }: Props) {
	const { t } = useTranslation();
	const { workbenchIndex } = useContext(WorkbenchContext);

	const portal = document.querySelector(
		`#workbench_${workbenchIndex}_sidebar`
	);

	const [disableLock, setDisableLock] = useState(false);

	// Handle document clicks
	const handleMainClick = useCallback(() => {
		// disable the focus lock when activity main is clicked
		setDisableLock(true);
	}, []);

	const handleSidebarClick = useCallback(() => {
		// enable the focus lock when activity sidebar is clicked
		setDisableLock(false);
	}, []);

	// Handle focus within the edit form
	const handleFocus = useCallback(() => {
		if (disableLock) {
			setDisableLock(false);
		}
	}, [disableLock]);

	// Handle keydown for Escape and Tab key
	const handleKeyDown = useCallback(
		e => {
			if (e.key === "Escape") close();
		},
		[close]
	);

	const activityMain = document.querySelector(
		`.workbench-form_${workbenchIndex}`
	);

	// Setup document event listeners
	useEffect(() => {
		activityMain?.addEventListener("mousedown", handleMainClick, false);
		portal?.addEventListener("mousedown", handleSidebarClick, false);

		return () => {
			activityMain?.removeEventListener(
				"mousedown",
				handleMainClick,
				false
			);
			portal?.removeEventListener("mousedown", handleSidebarClick, false);
		};
	}, [activityMain, handleMainClick, handleSidebarClick, portal]);

	if (!portal) return null;

	return ReactDOM.createPortal(
		<FocusLock disabled={disableLock}>
			<div
				role="dialog"
				aria-labelledby="sidebar-edit-form__title"
				aria-describedby="sidebar-edit-form__desc"
				onKeyDown={handleKeyDown}
				onFocus={handleFocus}
				className="sidebar-edit-form">
				<div className="sidebar-edit-form__header">
					<h2
						className="sidebar-edit-form__title"
						id="sidebar-edit-form__title">
						{t("main.text.edit")}
					</h2>
					<IconButton
						className="btn-basic--secondary sidebar-edit-form__close"
						label={t("common.labels.close")}
						onClick={close}
						icon="times"
						iconWeight="l"
					/>
				</div>
				<div className="sidebar-edit-form__body">
					<p
						className="sidebar-edit-form__desc"
						id="sidebar-edit-form__desc">
						{t("elements.legacyArrayView.youAreEditing")}{" "}
						<span className="text-info">{title}</span>
					</p>
					<AutoFocusInside>
						<div className="activity-form">
							{loadingContent ? <Loading /> : children}
						</div>
					</AutoFocusInside>
				</div>
			</div>
		</FocusLock>,
		portal
	);
}

export default EditForm;
